import { useState } from "react";
import { Button } from "./Button";
import { GlowingEffect } from "./GlowingEffect";
import { cn } from "../lib/utils";

const HubSpotForm = ({
  portalId,
  formId,
  fields = [],
  submitButtonText = "Submit",
  successMessage = "Thanks! We'll be in touch soon.",
  className = "",
  buttonVariant = "default",
  labelClassName = "block text-sm font-medium text-gray-700",
  // Submit button styling is opt-in overridable; defaults preserve the original look
  // so existing pages are unaffected.
  buttonWrapperClassName = "relative border-2 border-blue-500 rounded-full hover:bg-blue-800 transition-colors duration-300 w-fit mx-auto",
  buttonClassName = "relative z-10 px-6 py-3 text-base rounded-full disabled:opacity-50 text-white",
  showButtonGlow = true,
  onSubmit,
  onSuccess,
  onError
}) => {
  const [formData, setFormData] = useState(() => {
    const initialData = {};
    fields.forEach(field => {
      initialData[field.name] = field.defaultValue || "";
    });
    return initialData;
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    if (onSubmit) {
      onSubmit(formData);
    }

    try {
      const hubspotFields = fields.map(field => ({
        name: field.name,
        value: formData[field.name]
      }));

      const res = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            fields: hubspotFields,
            context: {
              pageUri: window.location.href,
              pageName: document.title
            }
          })
        }
      );

      if (res.ok) {
        setSubmitted(true);
        if (onSuccess) {
          onSuccess(formData);
        }
      } else {
        const errorData = await res.json();
        console.error("HubSpot error:", errorData);
        setError("Submission failed. Please try again.");
        if (onError) {
          onError(errorData);
        }
      }
    } catch (err) {
      console.error("Submission failed:", err);
      setError("Submission failed. Please try again.");
      if (onError) {
        onError(err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const baseInputClasses = "block w-full";

    switch (field.type) {
      case "email":
        return (
          <input
            type="email"
            name={field.name}
            required={field.required}
            value={formData[field.name]}
            onChange={handleChange}
            placeholder={field.placeholder}
            className={cn(baseInputClasses, field.className)}
          />
        );
      case "tel":
        return (
          <input
            type="tel"
            name={field.name}
            required={field.required}
            value={formData[field.name]}
            onChange={handleChange}
            placeholder={field.placeholder}
            className={cn(baseInputClasses, field.className)}
          />
        );
      case "textarea":
        return (
          <textarea
            name={field.name}
            required={field.required}
            value={formData[field.name]}
            onChange={handleChange}
            placeholder={field.placeholder}
            rows={field.rows || 4}
            className={cn(baseInputClasses, field.className)}
          />
        );
      case "select":
        return (
          <select
            name={field.name}
            required={field.required}
            value={formData[field.name]}
            onChange={handleChange}
            className={cn(baseInputClasses, field.className)}
          >
            <option value="">Select an option</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type="text"
            name={field.name}
            required={field.required}
            value={formData[field.name]}
            onChange={handleChange}
            placeholder={field.placeholder}
            className={cn(baseInputClasses, field.className)}
          />
        );
    }
  };

  if (submitted) {
    return (
      <div className={cn("text-center", className)}>
        <div className="bg-green-500/20 border border-green-400/30 rounded-lg p-6">
          <div className="text-green-400 font-semibold text-lg mb-2">
            ✅ Message Sent!
          </div>
          <div className="text-green-200">
            {successMessage}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={cn("space-y-6 max-w-lg mx-auto", className)}>
      {fields.map((field, index) => (
        <div key={index}>
          <label className={labelClassName}>
            {field.label} {field.required && <span className="text-red-400">*</span>}
          </label>
          {renderField(field)}
        </div>
      ))}

      {error && (
        <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-3">
          <div className="text-red-400 text-sm font-medium">
            {error}
          </div>
        </div>
      )}

      <div className={buttonWrapperClassName}>
        {showButtonGlow && (
          <GlowingEffect
            disabled={false}
            glow={true}
            proximity={100}
            spread={30}
            blur={0.5}
            borderWidth={2}
            movementDuration={1}
            className="rounded-full"
          />
        )}
        <Button
          type="submit"
          disabled={isSubmitting}
          variant={buttonVariant}
          size="lg"
          className={buttonClassName}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </span>
          ) : (
            submitButtonText
          )}
        </Button>
      </div>
    </form>
  );
};

export default HubSpotForm;